import telegramStarsApi from './telegramStarsApi'
import sbpApi from './sbpApi'
import sbp2Api from './sbp2Api'
import cryptoApi from './cryptoApi'
import uzsApi from './uzsApi'
import kztApi from './kztApi'
import cardApi from './cardApi'
import cardwwApi from './cardwwApi'
import skinsApi from './skinsApi'
import apiService from '../../../services/api'
// import cryptoApi from './cryptoApi' // Будет добавлено позже
// import cardApi from './cardApi' // Будет добавлено позже

/**
 * Payment API Router
 * Общий роутер для всех способов оплаты
 * Направляет запросы к соответствующим API сервисам
 */

// Константы способов оплаты
export const PAYMENT_METHODS = {
  TELEGRAM_STARS: 'telegram_stars',
  SBP: 'sbp', // СБП - Система Быстрых Платежей (РФ)
  SBP2: 'sbp2', // СБП #2 - альтернативный метод
  CRYPTO: 'crypto', // Криптовалюта
  UZS: 'uzs', // Узбекистан (UZS) - P2P перевод на карту
  CARD: 'card', // Банковские карты
  CARDWW1: 'cardww1', // Международные банковские карты VISA/MC
  SKINS: 'skins', // Пополнение скинами Steam
  KZT1: 'kzt1' // Казахстан (KZT) - P2P перевод в тенге
}

// Метаданные способов оплаты
// Названия и описания берутся из переводов i18n (payment.methods.*)
export const PAYMENT_METHODS_META = {
  [PAYMENT_METHODS.TELEGRAM_STARS]: {
    id: PAYMENT_METHODS.TELEGRAM_STARS,
    icon: 'Star',
    available: true, // Уже реализовано
    minAmount: 100,
    maxAmount: 17500
  },
  [PAYMENT_METHODS.SBP]: {
    id: PAYMENT_METHODS.SBP,
    icon: 'Smartphone',
    available: true, // Реализовано
    minAmount: 100,
    maxAmount: 50000
  },
  [PAYMENT_METHODS.SBP2]: {
    id: PAYMENT_METHODS.SBP2,
    icon: 'Smartphone',
    available: true, // Реализовано
    minAmount: 100,
    maxAmount: 50000
  },
  [PAYMENT_METHODS.CRYPTO]: {
    id: PAYMENT_METHODS.CRYPTO,
    icon: 'Bitcoin',
    available: true, // Реализовано
    minAmount: 2,
    maxAmount: 100000
  },
  [PAYMENT_METHODS.UZS]: {
    id: PAYMENT_METHODS.UZS,
    icon: 'Uzbekistan',
    available: true, // Реализовано
    minAmount: 20000,
    maxAmount: 30000000
  },
  [PAYMENT_METHODS.CARD]: {
    id: PAYMENT_METHODS.CARD,
    icon: 'CreditCard',
    available: true, // Реализовано (card1)
    minAmount: 100,
    maxAmount: 50000
  },
  [PAYMENT_METHODS.CARDWW1]: {
    id: PAYMENT_METHODS.CARDWW1,
    icon: 'CreditCard',
    available: true, // Реализовано (cardww1)
    minAmount: 10,
    maxAmount: 5000
  },
  [PAYMENT_METHODS.SKINS]: {
    id: PAYMENT_METHODS.SKINS,
    icon: 'Steam',
    available: true, // Всегда доступно
    minAmount: 0,
    maxAmount: 0
  },
  [PAYMENT_METHODS.KZT1]: {
    id: PAYMENT_METHODS.KZT1,
    icon: 'Kazakhstan',
    available: true, // По задаче показываем всегда
    minAmount: 1000,
    maxAmount: 900000
  }
}

/**
 * Получить список доступных способов оплаты
 * @returns {Array} Массив доступных способов оплаты
 */
export const getAvailablePaymentMethods = () => {
  const methodsOrder = [
    PAYMENT_METHODS.TELEGRAM_STARS,
    PAYMENT_METHODS.SKINS,
    PAYMENT_METHODS.UZS,
    PAYMENT_METHODS.KZT1,
    PAYMENT_METHODS.SBP,
    PAYMENT_METHODS.SBP2,
    PAYMENT_METHODS.CARD,
    PAYMENT_METHODS.CARDWW1,
    PAYMENT_METHODS.CRYPTO
  ]

  const backendFlagToMethod = {
    stars: PAYMENT_METHODS.TELEGRAM_STARS,
    steam: PAYMENT_METHODS.SKINS,
    sbp1: PAYMENT_METHODS.SBP,
    sbp2: PAYMENT_METHODS.SBP2,
    crypto1: PAYMENT_METHODS.CRYPTO,
    uzs1: PAYMENT_METHODS.UZS,
    kzt1: PAYMENT_METHODS.KZT1,
    card1: PAYMENT_METHODS.CARD,
    cardww1: PAYMENT_METHODS.CARDWW1
  }

  const getMethodsByBackendAvailability = async () => {
    const response = await apiService.makeRequest('/api/topup/')
    const available = response?.available || {}

    const availableMethodsByBackend = new Set(
      Object.entries(available)
        .filter(([, isEnabled]) => isEnabled === true)
        .map(([backendFlag]) => backendFlagToMethod[backendFlag])
        .filter(Boolean)
    )

    return methodsOrder
      .map((methodId) => PAYMENT_METHODS_META[methodId])
      .filter((method) => method?.available && availableMethodsByBackend.has(method.id))
  }

  return getMethodsByBackendAvailability().catch((error) => {
    console.error('❌ Ошибка получения available-методов из /api/topup/:', error)

    // Fallback: показываем локально доступные методы в фиксированном порядке
    return methodsOrder
      .map((methodId) => PAYMENT_METHODS_META[methodId])
      .filter((method) => method?.available)
  })
}

/**
 * Получить пакеты для конкретного способа оплаты
 * @param {string} method - Способ оплаты (telegram_stars, sbp, crypto, card)
 * @returns {Promise<Array>} Массив пакетов
 */
export const fetchPackages = async (method) => {
  switch(method) {
    case PAYMENT_METHODS.TELEGRAM_STARS:
      return await telegramStarsApi.fetchCrystalPackages()
    
    case PAYMENT_METHODS.SBP:
      return await sbpApi.fetchSbpPackages()

    case PAYMENT_METHODS.SBP2:
      return await sbp2Api.fetchSbp2Packages()

    case PAYMENT_METHODS.CRYPTO:
      return await cryptoApi.fetchCryptoPackages()

    case PAYMENT_METHODS.UZS:
      return await uzsApi.fetchUzsPackages()

    case PAYMENT_METHODS.KZT1:
      return await kztApi.fetchKztPackages()

    case PAYMENT_METHODS.CARD:
      return await cardApi.fetchCardPackages()

    case PAYMENT_METHODS.CARDWW1:
      return await cardwwApi.fetchCardWwPackages()

    case PAYMENT_METHODS.SKINS:
      // Для пополнения скинами нет пакетов /api/topup/
      return []
    
    default:
      throw new Error(`Неизвестный способ оплаты: ${method}`)
  }
}

/**
 * Создать платеж для конкретного способа оплаты
 * @param {string} method - Способ оплаты
 * @param {Object} data - Данные для создания платежа
 * @returns {Promise<Object>} Данные платежа
 */
export const createPayment = async (method, data) => {
  switch(method) {
    case PAYMENT_METHODS.TELEGRAM_STARS:
      return await telegramStarsApi.createInvoice(data.crystals, data.stars)
    
    case PAYMENT_METHODS.SBP:
      // СБП поддерживает два варианта: по кристаллам или по рублям
      if (data.crystals) {
        return await sbpApi.createPaymentByCrystals(data.crystals)
      } else if (data.rubles) {
        return await sbpApi.createPaymentByRubles(data.rubles)
      } else {
        throw new Error('Для СБП требуется указать crystals или rubles')
      }

    case PAYMENT_METHODS.SBP2:
      // СБП #2 поддерживает два варианта: по кристаллам или по рублям
      if (data.crystals) {
        return await sbp2Api.createPaymentByCrystals(data.crystals)
      } else if (data.rubles) {
        return await sbp2Api.createPaymentByRubles(data.rubles)
      } else {
        throw new Error('Для СБП #2 требуется указать crystals или rubles')
      }

    case PAYMENT_METHODS.CRYPTO:
      // Crypto поддерживает два варианта: по кристаллам или по USD
      if (data.crystals) {
        return await cryptoApi.createPaymentByCrystals(data.crystals)
      } else if (data.dollars) {
        return await cryptoApi.createPaymentByDollars(data.dollars)
      } else {
        throw new Error('Для Crypto требуется указать crystals или dollars')
      }

    case PAYMENT_METHODS.UZS:
      // UZS поддерживает два варианта: по кристаллам или по UZS
      if (data.crystals) {
        return await uzsApi.createPaymentByCrystals(data.crystals)
      } else if (data.uzs) {
        return await uzsApi.createPaymentByUzs(data.uzs)
      } else {
        throw new Error('Для UZS требуется указать crystals или uzs')
      }

    case PAYMENT_METHODS.KZT1:
      // Для KZT1 отправляем только сумму в тенге
      if (data.kzt || data.kzt1) {
        return await kztApi.createPaymentByKzt(data.kzt || data.kzt1)
      }
      throw new Error('Для KZT1 требуется указать kzt')

    case PAYMENT_METHODS.CARD:
      // Card поддерживает два варианта: по кристаллам или по рублям
      if (data.crystals) {
        return await cardApi.createPaymentByCrystals(data.crystals)
      } else if (data.rubles) {
        return await cardApi.createPaymentByRubles(data.rubles)
      } else {
        throw new Error('Для Card требуется указать crystals или rubles')
      }

    case PAYMENT_METHODS.CARDWW1:
      // CardWW1 поддерживает два варианта: по кристаллам или по долларам
      if (data.crystals) {
        return await cardwwApi.createPaymentByCrystals(data.crystals)
      } else if (data.dollars) {
        return await cardwwApi.createPaymentByDollars(data.dollars)
      } else {
        throw new Error('Для CardWW1 требуется указать crystals или dollars')
      }

    case PAYMENT_METHODS.SKINS:
      return await skinsApi.createSkinsPaymentLink()
    
    default:
      throw new Error(`Неизвестный способ оплаты: ${method}`)
  }
}

/**
 * Открыть окно оплаты для конкретного способа
 * @param {string} method - Способ оплаты
 * @param {Object} paymentData - Данные платежа
 * @param {Function} onResult - Callback для результата
 * @param {Window} paymentWindow - Подготовленное окно оплаты для web-версии
 */
export const openPayment = (method, paymentData, onResult, paymentWindow = null) => {
  switch(method) {
    case PAYMENT_METHODS.TELEGRAM_STARS:
      telegramStarsApi.openTelegramPayment(paymentData.invoice_url, onResult)
      break
    
    case PAYMENT_METHODS.SBP:
      sbpApi.openSbpPayment(paymentData.link, onResult, paymentWindow)
      break

    case PAYMENT_METHODS.SBP2:
      sbp2Api.openSbp2Payment(paymentData.link, onResult, paymentWindow)
      break

    case PAYMENT_METHODS.CRYPTO:
      cryptoApi.openCryptoPayment(paymentData.link, onResult, paymentWindow)
      break

    case PAYMENT_METHODS.UZS:
      if (paymentData?.link) {
        uzsApi.openUzsPayment(paymentData.link, onResult, paymentWindow)
        break
      }

      if (onResult) onResult({ success: true })
      break

    case PAYMENT_METHODS.KZT1:
      kztApi.openKztPayment(paymentData.link, onResult, paymentWindow)
      break

    case PAYMENT_METHODS.CARD:
      cardApi.openCardPayment(paymentData.link, onResult, paymentWindow)
      break

    case PAYMENT_METHODS.CARDWW1:
      cardwwApi.openCardWwPayment(paymentData.link, onResult, paymentWindow)
      break

    case PAYMENT_METHODS.SKINS:
      skinsApi.openSkinsPayment(paymentData.link, onResult, paymentWindow)
      break
    
    // case PAYMENT_METHODS.CRYPTO:
    //   cryptoApi.openPayment(paymentData, onResult)
    //   break
    
    // case PAYMENT_METHODS.CARD:
    //   cardApi.openPayment(paymentData, onResult)
    //   break
    
    default:
      throw new Error(`Неизвестный способ оплаты: ${method}`)
  }
}

// Экспорт по умолчанию
export default {
  PAYMENT_METHODS,
  PAYMENT_METHODS_META,
  getAvailablePaymentMethods,
  fetchPackages,
  createPayment,
  openPayment
}
